import { AppTFunction } from '@retail/app/i18n';
import * as yup from 'yup';
import { ObjectSchema } from 'yup';

const baseItemSchema = (t: AppTFunction) =>
  yup.object({
    campaignUnit: yup.string().nullable().required(t('calculus.error.requiredField')),
    campaignPurchasePrice: yup
      .number()
      .nullable()
      .typeError(t('calculus.error.mustBeNumberOver0'))
      .transform((_, value: string | null) => {
        if (value) return Number((value + '').replace(',', '.'));
        return undefined;
      })
      .positive(t('calculus.error.mustBeNumberOver0'))
      .test('max-two-decimals', t('calculus.error.mustHaveMaxTwoDecimalsSimple'), (value) => {
        if (value === undefined || value === null) return true;
        const stringValue = value.toString();
        const decimalPart = stringValue.split('.')[1];
        return !decimalPart || decimalPart.length <= 2;
      }),
    campaignSalesPrice: yup
      .number()
      .nullable()
      .typeError(t('calculus.error.mustBeNumber'))
      .transform((_, value: string | null) => {
        if (value) {
          return Number((value + '').replace(',', '.'));
        }
        return null;
      })
      .positive(t('calculus.error.mustBeNumber'))

      .when('useDiscount', {
        is: true,
        then: (schema) =>
          schema
            .optional()
            .test('max-two-decimals', t('calculus.error.mustHaveMaxTwoDecimals'), (value) => {
              if (value === undefined || value === null) return true;
              const stringValue = value.toString();
              const decimalPart = stringValue.split('.')[1];
              return !decimalPart || decimalPart.length <= 2;
            }), // Hvis true: gjør feltet optional
        otherwise: (schema) =>
          schema
            .test('max-two-decimals', t('calculus.error.mustHaveMaxTwoDecimals'), (value) => {
              if (value === undefined || value === null) return false;
              const stringValue = value.toString();
              const decimalPart = stringValue.split('.')[1];
              return !decimalPart || decimalPart.length <= 2;
            })
            .required(t('calculus.error.requiredField')), // Hvis false: påkrevd
      }),
  });

export const brandCampaignItemSchema = (t: AppTFunction) =>
  baseItemSchema(t).concat(
    yup.object({
      fixedSalesPrice: yup.boolean().required(t('calculus.error.requiredField')),
      secondaryDiscountPercentage: yup
        .number()
        .nullable()
        .typeError(t('calculus.error.mustBeNumber'))
        .transform((_, value: string | null) => {
          if (value) {
            return Number((value + '').replace(',', '.'));
          }
          return null;
        })
        .positive(t('calculus.error.mustBeNumber'))
        .optional(),
      tertiaryDiscountPercentage: yup
        .number()
        .nullable()
        .typeError(t('calculus.error.mustBeNumber'))
        .transform((_, value: string | null) => {
          if (value) {
            return Number((value + '').replace(',', '.'));
          }
          return null;
        })
        .positive(t('calculus.error.mustBeNumber'))
        .optional(),
    })
  );

export const memberCampaignItemSchema = (t: AppTFunction) =>
  baseItemSchema(t).concat(
    yup.object({
      useDiscount: yup.boolean().default(false).required(t('calculus.error.requiredField')),
      primaryDiscountPercentage: yup
        .number()
        .nullable()
        .typeError(t('calculus.error.mustBeNumber'))
        .transform((_, value: string | null) => {
          if (value) {
            return Number((value + '').replace(',', '.'));
          }
          return null;
        })
        .positive(t('calculus.error.mustBeNumber'))
        .optional(),
    })
  );

export type BaseCampaignItemSchema = yup.InferType<ReturnType<typeof baseItemSchema>>;
export type BrandCampaignItemSchema = yup.InferType<ReturnType<typeof brandCampaignItemSchema>>;
export type MemberCampaignItemSchema = yup.InferType<ReturnType<typeof memberCampaignItemSchema>>;

export const addCampaignItemsSchema = (schema: ObjectSchema<any>) =>
  yup.object({
    items: yup.lazy((value) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const shape = Object.keys(value).reduce((acc, key) => {
          acc[key] = schema;
          return acc;
        }, {} as Record<string, any>);

        return yup.object().shape(shape);
      }

      return yup.object();
    }),
  });

export type AddCampaignItemsSchema<T extends BaseCampaignItemSchema> = {
  items: Record<string, T>;
};
